import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, EngineKeyline, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { fetchSavedAssets } from '@/data/sources-media';
import type { SavedAsset } from '@/types/media';
import { StockMediaPicker } from './StockMediaPicker';

/* /social/content-studio/stock — the AI Content Studio visual-brief step's
 * stock library. The Pexels picker (search + save) sits above a workspace-
 * scoped list of already-saved assets. Each saved asset now shows a real
 * thumbnail (image) / first-frame (video) from a fresh server-signed preview
 * URL and opens a Preview. To USE a saved asset in a design, open a draft and
 * pick it from the Media step's "Saved" tab (that is where a draft/page exists
 * to place it into). Attribution is shown on every saved asset. */

export function StockLibraryPage() {
  const saved = useQuery({ queryKey: ['saved-media'], queryFn: fetchSavedAssets });
  const items = saved.data?.items ?? [];
  const [preview, setPreview] = useState<SavedAsset | null>(null);

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title="Stock Media"
        subtitle="Search photos and videos for your content, and save them to this workspace."
        actions={<Badge tone="ai">AI Content Studio · Beta</Badge>}
      />
      <EngineKeyline className="mb-6" />

      <Card className="mb-8 p-4">
        <StockMediaPicker />
      </Card>

      <section aria-labelledby="saved-heading" className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 id="saved-heading" className="font-display text-lg font-semibold text-text-primary">
            Saved to this workspace
          </h2>
          <span className="text-xs text-text-secondary">Source: bc_media_assets</span>
        </div>
        {saved.isLoading ? (
          <LoadingState rows={2} />
        ) : saved.isError ? (
          <ErrorState error="Could not load saved media. Try again." onRetry={() => void saved.refetch()} />
        ) : items.length === 0 ? (
          <EmptyState
            title="No saved media yet"
            teach="Search above and select a photo or video — saved assets appear here, scoped to this workspace. To use one in a design, open a draft and pick it from the Media step's Saved tab."
          />
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {items.map((asset) => (
              <SavedCard key={asset.id ?? `${asset.providerAssetId}-${asset.rendition}`} asset={asset} onPreview={() => setPreview(asset)} />
            ))}
          </div>
        )}
      </section>

      {preview && <PreviewModal asset={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function SavedCard({ asset, onPreview }: { asset: SavedAsset; onPreview: () => void }) {
  const isVideo = asset.kind === 'video';
  return (
    <Card className="flex flex-col overflow-hidden">
      <button
        type="button"
        onClick={onPreview}
        className="relative block aspect-[4/3] w-full bg-inset outline-none focus:ring-2 focus:ring-ai"
        aria-label={`Preview ${isVideo ? 'video' : 'photo'} ${asset.providerAssetId}`}
      >
        {asset.previewUrl ? (
          isVideo ? (
            <video src={asset.previewUrl} className="h-full w-full object-cover" muted playsInline preload="metadata" />
          ) : (
            <img src={asset.previewUrl} alt={asset.attribution ?? 'Saved media'} loading="lazy" className="h-full w-full object-cover" />
          )
        ) : (
          <div className="flex h-full items-center justify-center text-xs text-text-secondary">No preview</div>
        )}
        <span className="pointer-events-none absolute left-2 top-2">
          <Badge tone={isVideo ? 'ai' : 'neutral'}>{isVideo ? 'Video' : 'Photo'}</Badge>
        </span>
      </button>
      <div className="flex items-center justify-between gap-2 p-3">
        <div className="min-w-0">
          <p className="truncate text-xs text-text-secondary">{asset.attribution ?? 'Stock media'}</p>
          <p className="truncate text-[11px] text-text-tertiary">
            {asset.mime}
            {asset.width && asset.height ? ` · ${asset.width}×${asset.height}` : ''}
          </p>
        </div>
        <Button variant="secondary" onClick={onPreview}>Preview</Button>
      </div>
    </Card>
  );
}

function PreviewModal({ asset, onClose }: { asset: SavedAsset; onClose: () => void }) {
  const isVideo = asset.kind === 'video';
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Saved media preview"
      onClick={onClose}
    >
      <Card className="max-h-[90vh] w-full max-w-2xl overflow-auto p-4" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge tone={isVideo ? 'ai' : 'neutral'}>{isVideo ? 'Video' : 'Photo'}</Badge>
            <span className="text-sm font-medium text-text-primary">{asset.providerAssetId}</span>
          </div>
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
        <div className="overflow-hidden rounded-md bg-inset">
          {asset.previewUrl ? (
            isVideo ? (
              <video src={asset.previewUrl} className="max-h-[60vh] w-full object-contain" controls muted playsInline preload="metadata" />
            ) : (
              <img src={asset.previewUrl} alt={asset.attribution ?? 'Saved media'} className="max-h-[60vh] w-full object-contain" />
            )
          ) : (
            <div className="flex h-40 items-center justify-center text-sm text-text-secondary">Preview not available on this backend.</div>
          )}
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-text-secondary">
          <dt className="text-text-tertiary">Type</dt><dd>{asset.mime || '—'}</dd>
          <dt className="text-text-tertiary">Dimensions</dt><dd>{asset.width && asset.height ? `${asset.width}×${asset.height}` : '—'}</dd>
          <dt className="text-text-tertiary">Rendition</dt><dd>{asset.rendition || '—'}</dd>
          <dt className="text-text-tertiary">Attribution</dt>
          <dd>
            {asset.attribution ?? '—'}
            {asset.photographerUrl ? (
              <> · <a href={asset.photographerUrl} target="_blank" rel="noreferrer" className="underline">credit</a></>
            ) : null}
          </dd>
        </dl>
        <p className="mt-3 text-[11px] text-text-tertiary">To use this asset in a design, open a draft and choose it from the Media step's Saved tab.</p>
      </Card>
    </div>
  );
}
